"use client"

import {
  navigateWithThreadPreservation,
  DashboardMetricCard,
  DashboardMetricGrid,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  DashboardStatsSkeleton,
  PaneResponsiveCardGrid,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@yeshuman/sdk'
import { useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Activity,
  FileText,
  HeartPulse,
  Lightbulb,
  MessageCircle,
  Stethoscope,
  Upload,
} from 'lucide-react'
import { useActivities } from '../hooks/use-activities'
import { useSymptoms } from '../hooks/use-symptoms'
import { useConditions } from '../hooks/use-conditions'
import { useInsights } from '../hooks/use-insights'
import { useHealthRecords } from '../hooks/use-health-records'
import { SymptomCard } from './symptom-card'
import { ConditionCard } from './condition-card'
import { InsightCard } from './insight-card'
import { TYPE_LABELS } from './health-record-display'

function startOfWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function countThisWeek(items: Array<{ date?: string; occurred_at?: string }>, dateField: 'date' | 'occurred_at'): number {
  const weekStart = startOfWeek(new Date())
  return items.filter((item) => {
    const raw = item[dateField]
    if (!raw) return false
    return new Date(raw) >= weekStart
  }).length
}

function PatientDashboardFirstRunGuidance({
  onStartConversation,
  onNavigate,
}: {
  onStartConversation?: (message: string) => void
  onNavigate: (path: string) => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Start building your health picture</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Log symptoms and activities, add health files for Lumie to file, or ask for a summary of
          what to discuss with your practitioner.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => onNavigate('/symptoms')}>
            <Stethoscope className="mr-2 h-4 w-4" aria-hidden />
            Log symptom
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => onNavigate('/records')}>
            <Upload className="mr-2 h-4 w-4" aria-hidden />
            Add health files
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onStartConversation?.(
                'Summarise my recent health activity and anything I should discuss with my practitioner.',
              )
            }
          >
            <MessageCircle className="mr-2 h-4 w-4" aria-hidden />
            Ask Lumie
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function PatientDashboardSections({
  onStartConversation,
}: {
  onStartConversation?: (message: string) => void
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { activities = [], isLoading: activitiesLoading } = useActivities({ limit: 50 })
  const { symptoms = [], isLoading: symptomsLoading } = useSymptoms({ limit: 50 })
  const { conditions = [], isLoading: conditionsLoading } = useConditions({ limit: 50 })
  const { insights = [], isLoading: insightsLoading } = useInsights({ limit: 20 })
  const {
    records = [],
    isLoading: recordsLoading,
  } = useHealthRecords({ limit: 100 })
  const {
    records: recentRecords = [],
    isLoading: recentRecordsLoading,
    error: recentRecordsError,
  } = useHealthRecords({ limit: 3 })

  const metricsLoading =
    activitiesLoading || symptomsLoading || conditionsLoading || recordsLoading || insightsLoading

  const activitiesThisWeek = useMemo(() => countThisWeek(activities, 'date'), [activities])
  const symptomsThisWeek = useMemo(() => countThisWeek(symptoms, 'occurred_at'), [symptoms])
  const recordsCount = records.length
  const insightsCount = insights.length

  const recentSymptoms = useMemo(
    () =>
      [...symptoms]
        .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
        .slice(0, 3),
    [symptoms],
  )
  const recentInsights = useMemo(() => insights.slice(0, 3), [insights])
  const managedConditions = useMemo(
    () =>
      conditions.filter(
        (c) => c.clinical_status === 'active' || c.clinical_status === 'monitoring',
      ),
    [conditions],
  )

  const allLibrariesEmpty =
    !metricsLoading &&
    recordsCount === 0 &&
    symptoms.length === 0 &&
    insights.length === 0 &&
    activities.length === 0

  const navigateTo = (path: string) => {
    navigateWithThreadPreservation(navigate, path, searchParams)
  }

  const metricItems = [
    activitiesThisWeek > 0
      ? {
          key: 'activities',
          title: 'Activities this week',
          value: activitiesThisWeek,
          icon: Activity,
        }
      : null,
    symptomsThisWeek > 0
      ? {
          key: 'symptoms',
          title: 'Symptoms this week',
          value: symptomsThisWeek,
          icon: Stethoscope,
        }
      : null,
    managedConditions.length > 0
      ? {
          key: 'conditions',
          title: managedConditions.length === 1 ? 'Condition managed' : 'Conditions managed',
          value: managedConditions.length,
          icon: HeartPulse,
        }
      : null,
    recordsCount > 0
      ? {
          key: 'records',
          title: recordsCount === 1 ? 'Health record' : 'Health records',
          value: recordsCount,
          icon: FileText,
        }
      : null,
    insightsCount > 0
      ? {
          key: 'insights',
          title: insightsCount === 1 ? 'Insight' : 'Insights',
          value: insightsCount,
          icon: Lightbulb,
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string
    title: string
    value: number
    icon: typeof Activity
  }>

  return (
    <div className="space-y-6">
      {metricsLoading ? (
        <DashboardStatsSkeleton count={4} maxColumns={4} columnLayout="density" />
      ) : metricItems.length > 0 ? (
        <DashboardMetricGrid maxColumns={4} columnLayout="density">
          {metricItems.map((metric) => (
            <DashboardMetricCard
              key={metric.key}
              title={metric.title}
              value={metric.value}
              icon={metric.icon}
            />
          ))}
        </DashboardMetricGrid>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => navigateTo('/symptoms')}>
          <Stethoscope className="mr-2 h-4 w-4" aria-hidden />
          Log symptom
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => navigateTo('/activity')}>
          <Activity className="mr-2 h-4 w-4" aria-hidden />
          Log activity
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => navigateTo('/records')}>
          <Upload className="mr-2 h-4 w-4" aria-hidden />
          Add health files
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            onStartConversation?.(
              'Summarise my recent health activity and anything I should discuss with my practitioner.',
            )
          }
        >
          <MessageCircle className="mr-2 h-4 w-4" aria-hidden />
          Ask Lumie
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => navigateTo('/consult')}>
          <HeartPulse className="mr-2 h-4 w-4" aria-hidden />
          Open consult assist
        </Button>
      </div>

      {recentRecordsLoading ? (
        <DashboardSectionSkeleton cards={3} />
      ) : (
        <section className="space-y-4" aria-labelledby="recent-records-heading">
          <DashboardSectionTitle
            id="recent-records-heading"
            title="Recent health records"
            action={
              <DashboardSectionAction onClick={() => navigateTo('/records')}>
                View all
              </DashboardSectionAction>
            }
          />

          {recentRecordsError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
              <p className="font-medium text-destructive">Could not load health records</p>
              <p className="mt-1 text-muted-foreground">
                {(recentRecordsError as Error).message ?? 'Something went wrong.'}
              </p>
            </div>
          ) : recentRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <FileText className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No health records yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Add files for Lumie to file and summarise.
              </p>
              <div className="mt-4">
                <DashboardSectionAction onClick={() => navigateTo('/records')}>
                  Add files
                </DashboardSectionAction>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {recentRecords.map((record) => (
                <Link
                  key={record.id}
                  to={`/records/${record.id}`}
                  className="flex items-start justify-between gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="truncate font-medium">{record.title}</p>
                    {record.summary ? (
                      <p className="line-clamp-2 text-sm text-muted-foreground">{record.summary}</p>
                    ) : null}
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {TYPE_LABELS[record.record_type]}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {symptomsLoading ? (
        <DashboardSectionSkeleton cards={3} grid />
      ) : (
        <section className="space-y-4" aria-labelledby="recent-symptoms-heading">
          <DashboardSectionTitle
            id="recent-symptoms-heading"
            title="Recent symptoms"
            action={
              <DashboardSectionAction onClick={() => navigateTo('/symptoms')}>
                View all
              </DashboardSectionAction>
            }
          />

          {recentSymptoms.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <Stethoscope className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No symptoms logged yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Track how you feel over time to spot patterns with Lumie.
              </p>
              <div className="mt-4">
                <DashboardSectionAction onClick={() => navigateTo('/symptoms')}>
                  Log symptom
                </DashboardSectionAction>
              </div>
            </div>
          ) : (
            <PaneResponsiveCardGrid>
              {recentSymptoms.map((symptom) => (
                <SymptomCard key={symptom.id} symptom={symptom} />
              ))}
            </PaneResponsiveCardGrid>
          )}
        </section>
      )}

      {!conditionsLoading && managedConditions.length > 0 ? (
        <section className="space-y-4" aria-labelledby="managed-conditions-heading">
          <DashboardSectionTitle
            id="managed-conditions-heading"
            title="Conditions you're managing"
            action={
              <DashboardSectionAction onClick={() => navigateTo('/conditions')}>
                View all
              </DashboardSectionAction>
            }
          />
          <PaneResponsiveCardGrid>
            {managedConditions.slice(0, 3).map((condition) => (
              <ConditionCard key={condition.id} condition={condition} />
            ))}
          </PaneResponsiveCardGrid>
        </section>
      ) : null}

      {insightsLoading ? (
        <DashboardSectionSkeleton cards={3} grid />
      ) : (
        <section className="space-y-4" aria-labelledby="recent-insights-heading">
          <DashboardSectionTitle
            id="recent-insights-heading"
            title="Recent insights"
            action={
              <DashboardSectionAction onClick={() => navigateTo('/insights')}>
                View all
              </DashboardSectionAction>
            }
          />

          {recentInsights.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
              <Lightbulb className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No insights yet</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Lumie surfaces patterns as you log symptoms, activities, and records.
              </p>
              <div className="mt-4">
                <DashboardSectionAction onClick={() => navigateTo('/insights')}>
                  View insights
                </DashboardSectionAction>
              </div>
            </div>
          ) : (
            <PaneResponsiveCardGrid>
              {recentInsights.map((insight) => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </PaneResponsiveCardGrid>
          )}
        </section>
      )}

      {allLibrariesEmpty ? (
        <PatientDashboardFirstRunGuidance
          onStartConversation={onStartConversation}
          onNavigate={navigateTo}
        />
      ) : null}
    </div>
  )
}
